import React from 'react';
import PropTypes from 'prop-types';
import { componentStyles } from '../theme';
import Pagination from './Pagination';

// Card Component
const Card = ({
  children,
  className = '',
  padding = 'p-6',
  shadow = 'md',
  ...props
}) => (
  <div
    className={`${componentStyles.card} ${padding} ${className}`}
    {...props}
  >
    {children}
  </div>
);

// Button Component
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  className = '',
  icon: Icon,
  iconPosition = 'left',
  ...props
}) => {
  const baseClasses = componentStyles.button[variant] || componentStyles.button.primary;

  const sizeClasses = {
    xs: 'text-xs px-2.5 py-1.5',
    sm: 'text-sm px-3 py-2',
    md: 'text-sm px-4 py-2.5',
    lg: 'text-base px-6 py-3',
  };

  const iconSize = {
    xs: 'h-3.5 w-3.5',
    sm: 'h-4 w-4',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  }[size];

  const iconClasses = `${loading ? 'opacity-0' : ''} ${iconSize} ${iconPosition === 'right' ? 'order-2 ml-2' : 'mr-2'
    }`;

  const defaultClasses = `${baseClasses} ${sizeClasses[size]} ${disabled || loading ? 'opacity-60 cursor-not-allowed' : ''
    } inline-flex items-center justify-center`;

  const finalClasses = className ? `${defaultClasses} ${className}` : defaultClasses;

  return (
    <button
      className={finalClasses}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className={`animate-spin ${iconSize} ${iconPosition === 'right' ? 'order-2 ml-2' : 'mr-2'}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {Icon && !loading && <Icon className={iconClasses} />}
      <span className={loading ? 'opacity-0' : 'opacity-100'}>{children}</span>
    </button>
  );
};

// Add prop types for better development
Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'success', 'ghost']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  icon: PropTypes.elementType,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  children: PropTypes.node.isRequired,
};

// Input Component
const Input = ({
  label,
  error,
  className = '',
  ...props
}) => (
  <div className={className}>
    {label && <label className={componentStyles.label}>{label}</label>}
    <input className={`${componentStyles.input} ${error ? 'border-error-500' : ''}`} {...props} />
    {error && <p className="mt-1 text-sm text-error-600">{error}</p>}
  </div>
);

// Page Header Component
const PageHeader = ({
  title,
  subtitle,
  children,
  actions
}) => (
  <div className="mb-8">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold text-neutral-900">{title}</h1>
        {subtitle && (
          <p className="mt-2 text-lg text-neutral-600">{subtitle}</p>
        )}
      </div>
      {actions && (
        <div className="flex flex-col sm:flex-row gap-3">
          {actions}
        </div>
      )}
    </div>
    {children}
  </div>
);

// Stats Card Component
const StatsCard = ({
  title,
  value,
  icon: Icon,
  color = 'primary',
  trend,
  className = '',
  ...props
}) => (
  <Card className={`relative overflow-hidden ${className}`} {...props}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-neutral-600">{title}</p>
        <p className="text-3xl font-bold text-neutral-900 mt-1">{value}</p>
        {trend && (
          <p className={`text-sm mt-1 ${trend > 0 ? 'text-success-600' : trend < 0 ? 'text-error-600' : 'text-neutral-600'
            }`}>
            {trend > 0 ? '+' : ''}{trend}% from last month
          </p>
        )}
      </div>
      {Icon && (
        <div className={`p-3 rounded-full ${color === 'primary' ? 'bg-primary-100 text-primary-600' :
            color === 'success' ? 'bg-success-100 text-success-600' :
              color === 'warning' ? 'bg-warning-100 text-warning-600' :
                'bg-neutral-100 text-neutral-600'
          }`}>
          <Icon className="w-6 h-6" />
        </div>
      )}
    </div>
  </Card>
);

// Loading Spinner Component
const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <svg
        className={`animate-spin text-primary-600 ${sizeClasses[size]}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
};

// Empty State Component
const EmptyState = ({
  icon: Icon,
  title,
  description,
  action
}) => (
  <div className="text-center py-12">
    {Icon && (
      <div className="mx-auto w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-neutral-400" />
      </div>
    )}
    <h3 className="text-lg font-medium text-neutral-900 mb-2">{title}</h3>
    <p className="text-neutral-500 mb-6 max-w-md mx-auto">{description}</p>
    {action}
  </div>
);

// Badge Component
const Badge = ({
  children,
  variant = 'neutral',
  className = ''
}) => {
  const variantClasses = {
    neutral: 'bg-neutral-100 text-neutral-800',
    primary: 'bg-primary-100 text-primary-800',
    success: 'bg-success-100 text-success-800',
    warning: 'bg-warning-100 text-warning-800',
    error: 'bg-error-100 text-error-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variantClasses[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Table Component
const Table = ({
  headers,
  data,
  loading = false,
  emptyMessage = 'No data available',
  className = ''
}) => (
  <div className={`bg-white rounded-xl shadow-sm border border-neutral-200 ${className}`}>
    <div className="overflow-x-auto w-full">
      <table className="min-w-full divide-y divide-neutral-200">
        <colgroup>
          {headers.map((_, index) => (
            <col key={index} className={index === 0 ? 'sticky left-0 z-10 bg-white' : ''} />
          ))}
        </colgroup>
        <thead className="bg-neutral-50">
          <tr>
            {headers.map((header, index) => (
              <th
                key={index}
                className={`px-1.5 py-1.5 text-left text-[10px] font-medium text-neutral-600 uppercase tracking-wider whitespace-nowrap ${index === 0 ? 'sticky left-0 z-20 bg-neutral-50' : ''}`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {loading ? (
            <tr>
              <td colSpan={headers.length} className="px-1.5 py-1.5 text-center">
                <LoadingSpinner />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="px-1.5 py-1.5 text-center text-neutral-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr key={index} className="hover:bg-neutral-50 border-b border-neutral-100 relative">
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`px-1.5 py-1.5 text-[11px] text-neutral-700 ${cellIndex === 0 ? 'sticky left-0 z-10 bg-white' : ''}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Modal Component
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  className = ''
}) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md mx-4',
    md: 'max-w-lg mx-4',
    lg: 'max-w-2xl mx-4',
    xl: 'max-w-4xl mx-4'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className={`relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all ${sizeClasses[size]} w-full ${className}`}>
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 pr-8">
                {title}
              </h3>
              <button
                onClick={onClose}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    'PENDING_APPROVAL': { variant: 'warning', label: 'Pending Approval' },
    'APPROVED': { variant: 'success', label: 'Approved' },
    'REJECTED': { variant: 'error', label: 'Rejected' },
    'CREATED': { variant: 'primary', label: 'Created' },
    'PAID': { variant: 'success', label: 'Paid' },
    'INVOICE_PAID': { variant: 'primary', label: 'Invoice Paid' },
    'TDS_PENDING': { variant: 'warning', label: 'TDS Pending' },
    'TDS_PAID': { variant: 'primary', label: 'TDS Paid' },
    'COMPLETED': { variant: 'success', label: 'Completed' },
    'UNKNOWN': { variant: 'neutral', label: 'Unknown' }
  };
  const upperStatus = status?.toUpperCase() || 'UNKNOWN';
  const config = statusConfig[upperStatus] || statusConfig['UNKNOWN'];

  return <Badge variant={config.variant}>{config.label}</Badge>;
};

// Export all components for easy importing
export {
  Card,
  Button,
  Input,
  PageHeader,
  StatsCard,
  LoadingSpinner,
  EmptyState,
  Badge,
  Table,
  Pagination,
  Modal,
  StatusBadge
};
